import { z } from 'zod/v4';

import { analyticsSettingsSchemaLegacy } from '../../../common/schemas/legacy/analytics';
import { buttonHorizontalPositionSchema } from '../../../common/schemas/legacy/button-settings';
import { extendedPositionSchemaLegacy } from '../../../common/schemas/legacy/extended-position';
import { visibilityPluginShapeLegacy } from '../../../common/schemas/legacy/visibility';
import { widgetPositionSchemaLegacy } from '../../../common/schemas/legacy/widget-position';

// The login-edu widget renders the same form machinery as the subscription-form
// and payment-form widgets — same `sp-form-*` editors, same rendered controls —
// so it reuses the form widget's element and settings schemas rather than
// duplicating them. `formViewSchema` is literally `ELoginEduView`
// (`'static' | 'modal'`).
import { formViewSchema } from '../../../form/schemas/legacy/data';
import { formElementSchema } from '../../../form/schemas/legacy/form-element';
import { formAdditionalSettingsSchema, formInputSettingsSchema } from '../../../form/schemas/legacy/form-settings';

import { loginEduButtonSchema } from './button';
import { loginEduCheckboxSchema } from './checkbox';
import { courseTariffDetailsSchemaLegacy, eduCourseSchemaLegacy } from './course';

/**
 * Which form the widget shows first — mirrors `ELoginEduDefaultForm`.
 */
export const loginEduDefaultFormSchema = z.enum(['register', 'login']);

/**
 * Field shape shared by both placement variants of the login-edu widget —
 * mirrors `LoginEduWidgetData`
 * (`apps/websites/src/app/widgets/login-edu/type/login-edu-widget.type.ts`) and
 * the defaults produced by `loginEduData()` in the host model. The variants
 * diverge **only on `position`**: default placement keeps the simple per-device
 * grid, zero placement uses the extended-position grid.
 *
 * Scalars the host factory seeds with `null` are `nullable()`; groups and flags
 * added over the widget's lifetime are `nullish()`, because widgets persisted
 * before those fields existed omit them entirely.
 */
const loginEduCommonDataShape = {
    // Course binding
    course: eduCourseSchemaLegacy.nullable(),
    courseTariffId: z.number().nullish(),
    courseTariffDetails: courseTariffDetailsSchemaLegacy.nullish(),

    // Buttons & consent checkboxes
    buttons: z.array(loginEduButtonSchema),
    checkboxes: z.array(loginEduCheckboxSchema),
    registrationCheckbox: z.boolean().nullable(),

    // Form content
    signUpElements: z.array(formElementSchema),
    signInElements: z.array(formElementSchema),

    // Presentation
    horizontalPosition: buttonHorizontalPositionSchema.nullable(),
    formView: formViewSchema.nullable(),
    defaultForm: loginEduDefaultFormSchema.nullable(),
    linkBetweenForms: z.boolean().nullable(),
    inputSettings: formInputSettingsSchema,
    additionalFormSettings: formAdditionalSettingsSchema,
    analyticsSettings: analyticsSettingsSchemaLegacy.nullish(),

    // Visibility
    ...visibilityPluginShapeLegacy,
} as const;

/**
 * Data shape for a **default-placement** login-edu widget — common fields plus
 * the simple per-device position grid.
 */
export const loginEduLegacyDefaultDataSchema = z.object({
    ...loginEduCommonDataShape,
    position: widgetPositionSchemaLegacy,
});

/**
 * Data shape for a **zero-block-placement** login-edu widget — common fields
 * plus the extended-position grid (per-device width/height/x/y/rotate/zIndex).
 *
 * `isSynchronizeStyle` only exists on the zero variant (the per-device style
 * sync toggle the zero host renders), matching the form/payment-form widgets.
 */
export const loginEduLegacyZeroDataSchema = z.object({
    ...loginEduCommonDataShape,
    isSynchronizeStyle: z.boolean().nullish(),
    position: extendedPositionSchemaLegacy,
});

export type LoginEduDefaultDataLegacy = z.infer<typeof loginEduLegacyDefaultDataSchema>;
export type LoginEduZeroDataLegacy = z.infer<typeof loginEduLegacyZeroDataSchema>;
